import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { theme } from '../styles/theme';
import { useHeroArtwork } from '../hooks/useHeroArtwork';
import { HeroArtworkItem } from '../types/websiteHero.types';

interface HeroProps {
  onNavigateToLogin?: () => void;
}

interface CarouselPosition {
  index: number;
  offset: number;
  phase: 'idle' | 'dragging' | 'settling';
  duration: number;
}

interface DragGesture {
  pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  lastTime: number;
  velocity: number;
  offset: number;
  step: number;
  axis: 'horizontal' | 'vertical' | null;
}

const SNAP_DURATION = 380;
const AUTOPLAY_DURATION = 800;
const AUTOPLAY_DELAY = 4500;
const IMAGE_PREPARATION_TIMEOUT = 10000;
const IMAGE_RETRY_DELAY = 500;
const IMAGE_PREPARATION_ATTEMPTS = 2;
const wrapIndex = (index: number, count: number) => ((index % count) + count) % count;

interface HeroArtworkEntry {
  key: string;
  example?: HeroArtworkItem;
}

const artworkKey = (example: HeroArtworkItem, copy: number) => JSON.stringify([example.id, copy]);
const createArtworkEntries = (examples: HeroArtworkItem[]): HeroArtworkEntry[] => examples.length
  ? Array.from({ length: examples.length > 1 ? examples.length * Math.ceil(5 / examples.length) : 1 }, (_, index) => ({
    key: artworkKey(examples[index % examples.length], Math.floor(index / examples.length)),
    example: examples[index % examples.length],
  }))
  : [{ key: 'placeholder' }];

/** Decode the actual rendered image offscreen; retain the last ready artwork. */
const HeroArtwork: React.FC<{
  example?: HeroArtworkItem;
  artworkKey: string;
  onReady: (artworkKey: string, imageUrl: string) => void;
  onFailed: (artworkKey: string, imageUrl: string) => void;
  onDispose: (artworkKey: string) => void;
}> = ({ example, artworkKey, onReady, onFailed, onDispose }) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [displayed, setDisplayed] = useState<HeroArtworkItem>();
  const imageUrl = example?.imageUrl;
  const latestExample = useRef(example);
  latestExample.current = example;

  // Readiness belongs to this rendered node, not a future copy with the same key.
  useEffect(() => () => onDispose(artworkKey), [artworkKey, onDispose]);

  useEffect(() => {
    const image = imageRef.current;
    if (!image || !imageUrl) return;
    let cancelled = false;
    let generation = 0;
    let attempts = 0;
    let deadline: number | undefined;
    let retry: number | undefined;
    let removeListeners = () => {};
    const clearAttempt = () => {
      window.clearTimeout(deadline);
      removeListeners();
    };

    const startAttempt = () => {
      const attempt = ++generation;
      attempts += 1;
      let decoding = false;
      const isCurrent = () => !cancelled && generation === attempt && latestExample.current?.imageUrl === imageUrl;
      const fail = () => {
        if (!isCurrent()) return;
        generation += 1; // A timed-out decode must not settle a later attempt.
        clearAttempt();
        if (attempts < IMAGE_PREPARATION_ATTEMPTS) {
          retry = window.setTimeout(startAttempt, IMAGE_RETRY_DELAY);
        } else {
          onFailed(artworkKey, imageUrl);
        }
      };
      const prepare = async () => {
        if (!isCurrent() || decoding) return;
        decoding = true;
        try {
          if (typeof image.decode === 'function') await image.decode();
          if (!isCurrent()) return;
          if (!image.complete || !image.naturalWidth) { fail(); return; }
          generation += 1;
          clearAttempt();
          setDisplayed(latestExample.current);
          onReady(artworkKey, imageUrl);
        } catch {
          fail();
        }
      };
      image.addEventListener('load', prepare);
      image.addEventListener('error', fail);
      removeListeners = () => {
        image.removeEventListener('load', prepare);
        image.removeEventListener('error', fail);
      };
      // Covers both the download and decode, including a load event that never arrives.
      deadline = window.setTimeout(fail, IMAGE_PREPARATION_TIMEOUT);
      if (image.complete && image.naturalWidth) void prepare();
      else if (attempts > 1) image.src = imageUrl;
    };
    startAttempt();
    return () => {
      cancelled = true;
      generation += 1;
      clearAttempt();
      window.clearTimeout(retry);
    };
  }, [imageUrl, artworkKey, onReady, onFailed]);

  const artworks = example
    ? displayed && displayed.imageUrl !== example.imageUrl ? [displayed, example] : [example]
    : displayed ? [displayed] : [];

  return (
    <div className="hero-artwork" style={{ position: 'relative' }}>
      {artworks.map((artwork) => (
        <img
          key={artwork.imageUrl}
          ref={artwork.imageUrl === example?.imageUrl ? imageRef : undefined}
          src={artwork.imageUrl}
          alt={artwork.name}
          draggable={false}
          loading="eager"
          decoding="async"
          style={{ position: 'absolute', inset: 0, visibility: artwork.imageUrl === displayed?.imageUrl ? 'visible' : 'hidden' }}
        />
      ))}
      {!displayed && (
        <div className="hero-artwork-placeholder" role="img" aria-label={example?.name || 'Design preview'}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </div>
      )}
    </div>
  );
};

/** Presentation only: gestures and autoplay navigate the supplied Firebase array. */
export const HeroCarousel: React.FC<{ examples: HeroArtworkItem[] }> = ({ examples }) => {
  const [position, setPosition] = useState<CarouselPosition>({ index: 0, offset: 0, phase: 'idle', duration: SNAP_DURATION });
  const [rail, setRail] = useState(() => createArtworkEntries(examples));
  const [failedUrls, setFailedUrls] = useState<Set<string>>(() => new Set());
  const [isInteracting, setIsInteracting] = useState(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [pageHidden, setPageHidden] = useState(() => document.hidden);
  const gesture = useRef<DragGesture | null>(null);
  const snapTimer = useRef<number | null>(null);
  const pendingSnap = useRef<{ direction: number; duration: number } | null>(null);
  const decodedImages = useRef(new Map<string, string>());
  const [readyVersion, setReadyVersion] = useState(0);
  const desiredRail = (() => {
    const available = examples.filter(example => !failedUrls.has(example.imageUrl));
    if (!available.length) return [];
    if (available.length === 1) {
      // Retain the exact decoded copy in the phone when collapsing to a static design.
      const center = rail[wrapIndex(position.index, rail.length)];
      const existing = [center, ...rail].find(entry => entry.example?.id === available[0].id &&
        decodedImages.current.get(entry.key) === available[0].imageUrl);
      if (existing) return [{ ...existing, example: available[0] }];
    }
    return createArtworkEntries(available);
  })();
  const railExamples = Array.from(new Map(rail.flatMap(entry => entry.example ? [[entry.example.id, entry.example] as const] : [])).values());
  const count = railExamples.length;
  // Keep artwork nodes keyed to their media, not to a changing screen slot.
  // Short lists need repeated copies so both sides can loop without remounts.
  const artworkCount = rail.length;
  const activeIndex = Math.max(0, railExamples.findIndex(example => example.id === rail[wrapIndex(position.index, artworkCount)].example?.id));
  const autoplayEnabled = count > 1 && !autoplayPaused && !reducedMotion && !pageHidden;

  const markReady = useCallback((key: string, imageUrl: string) => {
    decodedImages.current.set(key, imageUrl);
    setReadyVersion((previous) => previous + 1);
  }, []);

  const markFailed = useCallback((key: string, imageUrl: string) => {
    if (decodedImages.current.get(key) === imageUrl) decodedImages.current.delete(key);
    setFailedUrls(previous => previous.has(imageUrl) ? previous : new Set(previous).add(imageUrl));
  }, []);

  const forgetArtwork = useCallback((key: string) => {
    decodedImages.current.delete(key);
  }, []);

  useLayoutEffect(() => {
    if (position.phase !== 'idle' || isInteracting || gesture.current || snapTimer.current !== null) return;
    const center = rail[wrapIndex(position.index, rail.length)];
    const isReady = (entry: HeroArtworkEntry) => !!entry.example && decodedImages.current.get(entry.key) === entry.example.imageUrl;
    // A same-ID URL refresh may already have decoded in the retained center node.
    const hasDecodedCenter = decodedImages.current.has(center.key);
    let nextRail = desiredRail;
    if (!nextRail.length) {
      const retained = [center, ...rail].find(isReady);
      nextRail = retained ? [retained] : [rail[0]];
    }
    let nextIndex = nextRail.findIndex(entry => entry.key === center.key && entry.example?.imageUrl === center.example?.imageUrl);
    if (nextIndex < 0) {
      nextIndex = nextRail.findIndex(isReady);
      // New candidates prepare outside the visible rail; never evict a decoded center for a placeholder.
      if (nextIndex < 0 && hasDecodedCenter) return;
      nextIndex = Math.max(0, nextIndex);
    }
    if (rail.length === nextRail.length && rail.every((entry, index) => entry.key === nextRail[index].key && entry.example === nextRail[index].example)) return;
    // Filtering can introduce a new repeated side copy. Decode the complete
    // replacement composition before changing any of the visible posters.
    const visibleSlots = nextRail.length > 1 ? [-1, 0, 1] : [0];
    if (hasDecodedCenter && !visibleSlots.every(slot => isReady(nextRail[wrapIndex(nextIndex + slot, nextRail.length)]))) return;
    pendingSnap.current = null;
    setRail(nextRail);
    setPosition(previous => ({ ...previous, index: nextIndex, offset: 0, phase: 'idle' }));
  }, [desiredRail, rail, position.index, position.phase, isInteracting, readyVersion]);

  const canMove = useCallback((direction: number) => {
    if (!count) return false;
    // Check every incoming visible slot, including the new outer side poster.
    return [-1, 0, 1].every((slot) => {
      const artworkIndex = wrapIndex(position.index + direction + slot, artworkCount);
      const entry = rail[artworkIndex];
      return !!entry.example && decodedImages.current.get(entry.key) === entry.example.imageUrl;
    });
  }, [artworkCount, count, rail, position.index]);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(preference.matches);
    const updateVisibility = () => setPageHidden(document.hidden);
    preference.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      preference.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => () => {
    if (snapTimer.current !== null) window.clearTimeout(snapTimer.current);
    snapTimer.current = null;
    pendingSnap.current = null;
  }, []);

  const snap = useCallback((direction: number, requestedDuration = SNAP_DURATION) => {
    if (count < 2 || snapTimer.current !== null || gesture.current) return;
    const waiting = direction !== 0 && !canMove(direction);
    if (waiting) {
      pendingSnap.current = { direction, duration: requestedDuration };
      if (position.phase !== 'dragging') return;
    } else {
      pendingSnap.current = null;
    }
    // If a drag reverses toward an unready poster, settle back while it loads.
    const nextDirection = waiting ? 0 : direction;
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : waiting ? SNAP_DURATION : requestedDuration;
    setPosition((previous) => ({ ...previous, offset: -nextDirection, phase: 'settling', duration }));
    snapTimer.current = window.setTimeout(() => {
      // Rebase positions without replacing the decoded image DOM nodes.
      setPosition((previous) => ({
        ...previous,
        index: wrapIndex(previous.index + nextDirection, artworkCount),
        offset: 0,
        phase: 'idle',
      }));
      snapTimer.current = null;
    }, duration);
  }, [artworkCount, canMove, count, position.phase]);

  useEffect(() => {
    const pending = pendingSnap.current;
    if (!pending || isInteracting || position.phase !== 'idle') return;
    if (pending.duration === AUTOPLAY_DURATION && !autoplayEnabled) {
      pendingSnap.current = null;
      return;
    }
    snap(pending.direction, pending.duration);
  }, [readyVersion, isInteracting, position.phase, autoplayEnabled, snap]);

  useEffect(() => {
    if (!autoplayEnabled || isInteracting || position.phase !== 'idle') return;
    // Each settled design gets a fresh viewing interval, including after a
    // drag, swipe or button press. Autoplay never fetches or changes media.
    const timer = window.setTimeout(() => {
      if (!pendingSnap.current) snap(1, AUTOPLAY_DURATION);
    }, AUTOPLAY_DELAY);
    return () => window.clearTimeout(timer);
  }, [autoplayEnabled, isInteracting, position.index, position.phase, snap]);

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      count < 2 || position.phase !== 'idle' || gesture.current ||
      !event.isPrimary || event.button !== 0 ||
      (event.target as HTMLElement).closest('button')
    ) return;

    pendingSnap.current = null;
    gesture.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
      offset: 0,
      step: Math.max(event.currentTarget.getBoundingClientRect().width * 0.32, 1),
      axis: null,
    };
    setIsInteracting(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = gesture.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 6) {
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }
    if (drag.axis !== 'horizontal') return;
    if (event.cancelable) event.preventDefault();

    const elapsed = event.timeStamp - drag.lastTime;
    if (elapsed > 0) drag.velocity = (event.clientX - drag.lastX) / elapsed;
    drag.lastX = event.clientX;
    drag.lastTime = event.timeStamp;
    drag.offset = Math.max(-1, Math.min(1, dx / drag.step));
    // Keep the current composition while a requested neighbor is decoding.
    // Pointer tracking continues so releasing still queues the intended swipe.
    if (drag.offset && !canMove(drag.offset < 0 ? 1 : -1)) return;
    setPosition((previous) => ({ ...previous, offset: drag.offset, phase: 'dragging' }));
  };

  const finishDrag = (event: React.PointerEvent<HTMLDivElement>, cancelled = false) => {
    const drag = gesture.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    gesture.current = null;
    setIsInteracting(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (drag.axis !== 'horizontal') return;

    const velocity = event.timeStamp - drag.lastTime < 100 ? drag.velocity : 0;
    const travelled = Math.abs(drag.offset * drag.step);
    const shouldAdvance = !cancelled && (
      Math.abs(drag.offset) >= 0.2 ||
      (travelled > 12 && Math.abs(velocity) > 0.45 && Math.sign(velocity) === Math.sign(drag.offset))
    );
    snap(shouldAdvance ? (drag.offset < 0 ? 1 : -1) : 0);
  };

  return (
    <div
      className={'hero-carousel' + (position.phase === 'dragging' ? ' is-dragging' : '') + (count > 1 ? ' is-interactive' : '')}
      role="region"
      aria-roledescription="carousel"
      aria-label={`Example designs. Arrow keys browse; Space pauses or resumes automatic playback. Automatic playback ${autoplayPaused ? 'paused' : 'enabled'}.`}
      tabIndex={count > 1 ? 0 : undefined}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={(event) => finishDrag(event)}
      onPointerCancel={(event) => finishDrag(event, true)}
      onLostPointerCapture={(event) => finishDrag(event, true)}
      onDragStart={(event) => event.preventDefault()}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && event.key === ' ') {
          event.preventDefault();
          if (!event.repeat) setAutoplayPaused(value => !value);
          return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          if (position.phase === 'idle' && !gesture.current) snap(event.key === 'ArrowRight' ? 1 : -1);
        }
      }}
    >
      <div className="hero-imagery-glow" aria-hidden="true" />

      <div className="hero-phone" aria-hidden="true">
        <span className="hero-phone-button hero-phone-button-left" />
        <span className="hero-phone-button hero-phone-button-right" />
        <div className="hero-phone-screen" />
      </div>

      {Array.from(new Map([...rail, ...desiredRail].map(entry => [entry.key, entry])).values()).map((entry) => {
        const artworkIndex = rail.findIndex(artwork => artwork.key === entry.key);
        const slot = artworkIndex < 0 ? 3 : count > 1 ? wrapIndex(artworkIndex - position.index + 2, artworkCount) - 2 : 0;
        const relative = slot + position.offset;
        const distance = Math.min(Math.abs(relative), 1);
        const example = entry.example;
        // At the center, match the device's screen bounds exactly; interpolate
        // back to the adjacent poster dimensions without changing navigation.
        const style: React.CSSProperties = {
          left: (50 + relative * 32) + '%',
          top: (49 + distance * 2.5) + '%',
          width: 'calc(' + (43 - distance * 5.5) + '% - var(--hero-phone-inset) * ' + (1 - distance) + ')',
          height: 'calc(' + (94 - distance * 16) + '% - var(--hero-phone-inset) * ' + (1 - distance) + ')',
          borderRadius: 'calc(' + (13 * (1 - distance)) + '% + ' + (2 * distance) + 'px) / calc(' + (7 * (1 - distance)) + '% + ' + (2 * distance) + 'px)',
          transform: 'translate(-50%, -50%) perspective(900px) rotateY(' + (-relative * 12) + 'deg) rotateZ(' + (-3 - relative * 3) + 'deg)',
          opacity: Math.max(0, 1 - Math.max(0, Math.abs(relative) - 1) * 2),
          zIndex: Math.round(10 - distance * 8),
          transition: position.phase === 'settling'
            ? ['left', 'top', 'width', 'height', 'border-radius', 'transform', 'opacity']
              .map((property) => property + ' ' + position.duration + 'ms ' + (position.duration === AUTOPLAY_DURATION ? 'cubic-bezier(.4,0,.2,1)' : 'cubic-bezier(.22,.68,0,1)'))
              .join(', ')
            : 'none',
        };
        return (
          <div key={entry.key} className="hero-design" style={style} aria-hidden={slot !== 0}>
            <HeroArtwork example={example} artworkKey={entry.key} onReady={markReady} onFailed={markFailed} onDispose={forgetArtwork} />
          </div>
        );
      })}

      <div className="hero-carousel-caption">
        {count > 1 && (
          <div className="hero-carousel-controls">
            <button type="button" aria-label="Previous design" disabled={position.phase !== 'idle'} onClick={() => snap(-1)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 6-6 6 6 6" /></svg>
            </button>
            {!reducedMotion && (
              <button
                type="button"
                aria-label={autoplayPaused ? 'Play carousel' : 'Pause carousel'}
                title={autoplayPaused ? 'Play carousel' : 'Pause carousel'}
                onClick={() => setAutoplayPaused((previous) => !previous)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={autoplayPaused ? 'm9 5 10 7-10 7Z' : 'M9 5v14M15 5v14'} />
                </svg>
              </button>
            )}
            <button type="button" aria-label="Next design" disabled={position.phase !== 'idle'} onClick={() => snap(1)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m10 6 6 6-6 6" /></svg>
            </button>
          </div>
        )}
      </div>
      <span className="hero-sr-only" aria-live={autoplayEnabled ? 'off' : 'polite'} aria-atomic="true">
        {count ? 'Design ' + (activeIndex + 1) + ' of ' + count + ': ' + railExamples[activeIndex].name : ''}
      </span>
    </div>
  );
};

// Verified public listings; keep these local so other download entry points stay unchanged.
export const HERO_CAROUSEL_STYLES = `
        .matchday-hero .hero-carousel {
          --hero-phone-inset: 18px;
          position: relative;
          width: 100%;
          aspect-ratio: 1.1;
          min-width: 0;
          user-select: none;
          -webkit-user-select: none;
          touch-action: pan-y pinch-zoom;
          -webkit-tap-highlight-color: transparent;
        }
        .matchday-hero .hero-carousel.is-interactive { cursor: grab; }
        .matchday-hero .hero-carousel.is-dragging { cursor: grabbing; }
        .matchday-hero .hero-carousel:focus-visible { outline: 2px solid #ff3474; outline-offset: 6px; border-radius: 24px; }
        .matchday-hero .hero-imagery-glow {
          position: absolute;
          inset: 0 -8% -5%;
          border-radius: 50%;
          background: radial-gradient(ellipse, rgba(255,39,0,.23), rgba(255,0,168,.09) 43%, transparent 72%);
          filter: blur(35px);
          pointer-events: none;
        }
        .matchday-hero .hero-design {
          position: absolute;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.22);
          border-radius: 2px;
          background: #090a0c;
          box-shadow: 0 18px 32px rgba(0,0,0,.45);
          pointer-events: none;
          will-change: transform;
        }
        .matchday-hero .hero-artwork { width: 100%; height: 100%; }
        .matchday-hero .hero-artwork img { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center; pointer-events: none; }
        .matchday-hero .hero-artwork-placeholder {
          display: grid;
          place-items: center;
          width: 100%;
          height: 100%;
          color: #ff3474;
          background: linear-gradient(135deg, rgba(255,39,0,.4), rgba(255,0,168,.2));
        }
        .matchday-hero .hero-phone {
          position: absolute;
          left: 50%;
          top: 49%;
          width: 43%;
          height: 94%;
          transform: translate(-50%, -50%) rotate(-3deg);
          z-index: 3;
          padding: calc(var(--hero-phone-inset) / 2 - 2px);
          border: 2px solid #b3b6b6;
          border-radius: 16% / 8%;
          background: linear-gradient(110deg, #747876, #08090a 6%, #090a0b 90%, #a4a7a5 97%, #26282a);
          box-shadow: inset 0 0 0 2px #232628, 0 0 0 2px #151718, 12px 18px 28px rgba(0,0,0,.75), 0 0 60px rgba(255,39,0,.2), 0 0 120px rgba(255,0,168,.12);
          pointer-events: none;
        }
        .matchday-hero .hero-phone-screen {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border: 1px solid #343839;
          border-radius: 13% / 7%;
          background: linear-gradient(140deg, #141a1e, #070b0d 60%, #10161a);
        }
        .matchday-hero .hero-phone-button { position: absolute; width: 3px; background: linear-gradient(#666,#babfbd,#353838); border-radius: 2px; }
        .matchday-hero .hero-phone-button-left { left: -5px; top: 22%; height: 11%; }
        .matchday-hero .hero-phone-button-right { right: -5px; top: 26%; height: 15%; }
        .matchday-hero .hero-carousel-caption { position: absolute; bottom: -3%; right: 1%; z-index: 20; display: flex; align-items: center; gap: 12px; color: #a9adb4; font-size: 11px; }
        .matchday-hero .hero-carousel-controls { display: none; gap: 5px; }
        .matchday-hero .hero-carousel-controls button { display: grid; place-items: center; width: 30px; height: 30px; padding: 6px; border: 1px solid #363a3e; border-radius: 50%; background: #090b0d; color: #e6e8eb; transition: border-color 180ms ease, color 180ms ease; }
        .matchday-hero .hero-carousel-controls button:hover { color: #ff3474; border-color: #ff3474; }
        .matchday-hero .hero-carousel-controls button:disabled { cursor: default; opacity: .55; }
        .matchday-hero .hero-carousel-controls svg { width: 100%; height: 100%; fill: none; stroke: currentColor; stroke-width: 1.7; }
        .matchday-hero .hero-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }

`;

const HERO_APP_STORE_URL = 'https://apps.apple.com/app/id6758546772';
const HERO_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.fourkmatchapp';

export const Hero: React.FC<HeroProps> = () => {
  // The website-only source is activated separately after its permission review.
  const { data: heroExamples = [] } = useHeroArtwork();
  const downloadStackRef = useRef<HTMLDivElement>(null);
  const qrLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const stack = downloadStackRef.current;
    const qr = qrLinkRef.current;
    if (!stack || !qr) return;

    // The badge links already match their artwork's visible bounds, excluding
    // transparent padding. Measure that full stack without resizing its layout.
    const matchStackHeight = () => {
      const height = stack.getBoundingClientRect().height;
      if (height > 0) qr.style.setProperty('--hero-qr-size', `${height}px`);
    };
    matchStackHeight();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', matchStackHeight);
      return () => window.removeEventListener('resize', matchStackHeight);
    }
    const observer = new ResizeObserver(matchStackHeight);
    observer.observe(stack);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="matchday-hero" aria-labelledby="matchday-hero-heading">
      <div className="hero-bg-orb hero-bg-orb-1" aria-hidden="true" />
      <div className="hero-bg-orb hero-bg-orb-2" aria-hidden="true" />
      <div className="hero-bg-orb hero-bg-orb-3" aria-hidden="true" />
      <div className="hero-mesh" aria-hidden="true" />
      <div className="hero-particles" aria-hidden="true">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={'hero-particle hero-particle-' + (i + 1)} />
        ))}
      </div>

      <div className="hero-grid">
        <div className="hero-content">
          <h1 id="matchday-hero-heading">
            <span>PRO-DESIGNED MATCHDAY GRAPHICS.</span>
            <span className="hero-headline-accent">READY IN SECONDS.</span>
          </h1>
          <ul className="hero-benefits">
            {['Your photos, professionally enhanced', 'Your team’s kit', 'Your match details included'].map((benefit) => (
              <li key={benefit}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="m5 12 4 4L19 6" />
                </svg>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
          <p className="hero-setup-copy">Set up once. Create in one click.</p>
          <div className="hero-actions">
            <div className="hero-download-stack" ref={downloadStackRef}>
              <a className="hero-download" href={HERO_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                Download the app
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M4 12h16m-7-7 7 7-7 7" />
                </svg>
              </a>
              <div className="hero-store-badges" aria-label="Download 4KMatch">
                <a className="hero-app-store-link" href={HERO_APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Download 4KMatch on the App Store (opens in a new tab)">
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="hero-app-store-badge"
                    width="120"
                    height="40"
                  />
                </a>
                <a className="hero-play-store-link" href={HERO_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Get 4KMatch on Google Play (opens in a new tab)">
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="hero-play-store-badge"
                    width="155"
                    height="60"
                  />
                </a>
              </div>
            </div>
            <a className="hero-download-qr" ref={qrLinkRef} href={HERO_APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Scan to download 4KMatch for iPhone, or open its App Store page">
              <QRCodeSVG value={HERO_APP_STORE_URL} size={128} level="M" marginSize={4} bgColor="#FFFFFF" fgColor="#000000" aria-hidden="true" />
            </a>
          </div>
        </div>

        <HeroCarousel
          key={JSON.stringify(heroExamples.map((example) => example.id))}
          examples={heroExamples}
        />
      </div>

      <style>{`
        .matchday-hero {
          position: relative;
          /* Let the existing foreground grid sit above the adjacent reaction layer. */
          isolation: auto;
          overflow: hidden;
          background: #030405;
          color: #fff;
          font-family: ${theme.fonts.primary};
          padding: 96px clamp(24px, 6vw, 88px) 32px;
        }
        .matchday-hero .hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr);
          align-items: center;
          width: 100%;
          max-width: 1320px;
          margin: 0 auto;
        }
        .matchday-hero .hero-content { position: relative; z-index: 20; padding: 0; }
        .matchday-hero h1 {
          margin: 0 0 16px;
          font-family: ${theme.fonts.heading};
          font-size: 56px;
          font-weight: 900;
          line-height: 1.05;
          letter-spacing: -.03em;
          text-transform: uppercase;
        }
        .matchday-hero h1 > span { display: block; text-wrap: balance; }
        .matchday-hero h1, .matchday-hero .hero-benefits li, .matchday-hero .hero-setup-copy { overflow-wrap: break-word; }
        .matchday-hero .hero-setup-copy { margin: 8px 0 0; max-width: 100%; color: #bec2c7; font-size: 16px; font-weight: 400; line-height: 1.5; }
        .matchday-hero .hero-headline-accent {
          margin-top: 8px;
          font-size: 36px;
          font-weight: 700;
          line-height: 1.1;
          letter-spacing: -.01em;
          background: linear-gradient(105deg, #ff403f 8%, #fa1582 95%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .matchday-hero .hero-benefits {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          margin: 0;
          padding: 0;
          list-style: none;
          color: #fff;
          font-size: 16px;
          font-weight: 500;
          line-height: 1.4;
          letter-spacing: normal;
        }
        .matchday-hero .hero-benefits li { display: grid; grid-template-columns: 16px minmax(0, 1fr); align-items: start; gap: 8px; max-width: 100%; }
        .matchday-hero .hero-benefits svg { margin-top: calc((1.4em - 16px) / 2); color: #ff3474; }
        .matchday-hero .hero-actions {
          --hero-download-gap: 12px;
          display: grid;
          grid-template-columns: minmax(0, 276px) 128px;
          grid-template-areas: "stack qr";
          align-items: start;
          gap: var(--hero-download-gap);
          width: 416px;
          max-width: 100%;
          margin: 16px 0 0;
        }
        .matchday-hero .hero-download-stack { grid-area: stack; display: grid; grid-template-columns: minmax(0, 1fr); grid-template-areas: "download" "badges"; align-content: start; gap: var(--hero-download-gap); min-width: 0; }
        .matchday-hero .hero-download {
          grid-area: download;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          box-sizing: border-box;
          gap: 15px;
          padding: 17px 16px;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 7px;
          background: linear-gradient(105deg, #ff443e, #fb178a);
          box-shadow: none;
          color: #fff;
          font-family: ${theme.fonts.primary};
          font-size: 16px;
          font-weight: 700;
          letter-spacing: .5px;
          text-transform: uppercase;
          line-height: 1.2;
          transition: transform 180ms ease, border-color 180ms ease;
        }
        .matchday-hero .hero-download:hover { transform: translateY(-2px); border-color: rgba(255,255,255,.25); }
        .matchday-hero .hero-download:active { transform: translateY(0); }
        /* Column ratios use visible badge bounds: Apple 119.66407 x 40; Google 564 x 168. */
        .matchday-hero .hero-store-badges { grid-area: badges; display: grid; grid-template-columns: 2.99160175fr 3.357142857fr; align-items: start; gap: var(--hero-download-gap); min-width: 0; }
        .matchday-hero .hero-store-badges a { display: block; position: relative; min-width: 0; }
        .matchday-hero .hero-app-store-link { aspect-ratio: 119.66407 / 40; }
        .matchday-hero .hero-play-store-link { aspect-ratio: 564 / 168; }
        .matchday-hero .hero-store-badges img { display: block; height: auto; }
        .matchday-hero .hero-app-store-badge { width: 100%; }
        /* Offset the full 646 x 250 PNG's 41px transparent margins; do not clip its artwork. */
        .matchday-hero .hero-play-store-badge { position: absolute; width: 114.539007092%; max-width: none; left: -7.269503546%; top: -24.404761905%; }
        .matchday-hero .hero-download-qr { grid-area: qr; align-self: start; justify-self: start; display: block; width: var(--hero-qr-size, 113.583024px); height: var(--hero-qr-size, 113.583024px); overflow: hidden; border-radius: 6px; background: #fff; text-decoration: none; }
        .matchday-hero .hero-download-qr svg { display: block; width: 100%; height: 100%; }
        .matchday-hero .hero-actions a:focus-visible { outline: 2px solid #ff3474; outline-offset: 4px; border-radius: 4px; }
        @media (max-width: 1279px) {
          .matchday-hero .hero-actions { grid-template-columns: minmax(0, 276px); grid-template-areas: "stack"; width: 276px; }
          .matchday-hero .hero-download-qr { display: none; }
        }

        ${HERO_CAROUSEL_STYLES}

        /* Preserve the animated background orbs and twelve brand particles. */
        .matchday-hero .hero-bg-orb { position: absolute; border-radius: 50%; filter: blur(100px); pointer-events: none; animation: matchdayHeroOrbFloat 20s ease-in-out infinite; }
        .matchday-hero .hero-bg-orb-1 { width: 800px; height: 800px; top: -30%; right: -15%; background: radial-gradient(circle, rgba(255,39,0,.15), transparent 70%); }
        .matchday-hero .hero-bg-orb-2 { width: 600px; height: 600px; bottom: -35%; left: -15%; background: radial-gradient(circle, rgba(255,0,168,.08), transparent 70%); animation-delay: -7s; }
        .matchday-hero .hero-bg-orb-3 { width: 400px; height: 400px; top: 30%; left: 40%; background: radial-gradient(circle, rgba(255,0,168,.08), transparent 70%); animation-delay: -14s; }
        .matchday-hero .hero-mesh { position: absolute; inset: 0; pointer-events: none; background: linear-gradient(135deg, rgba(255,39,0,.02), transparent 50%), linear-gradient(225deg, rgba(255,0,168,.02), transparent 50%); }
        .matchday-hero .hero-particles { position: absolute; inset: 0; pointer-events: none; overflow: hidden; opacity: .7; }
        .matchday-hero .hero-particle { position: absolute; width: 4px; height: 4px; border-radius: 50%; background: #ff2700; box-shadow: 0 0 10px rgba(255,39,0,.5); animation: matchdayHeroParticleFloat 15s ease-in-out infinite; }
        .matchday-hero .hero-particle:nth-child(3n + 2) { background: #ff00a8; box-shadow: 0 0 10px rgba(255,0,168,.5); }
        .matchday-hero .hero-particle-1 { top: 10%; left: 10%; animation-delay: 0s; }
        .matchday-hero .hero-particle-2 { top: 20%; right: 15%; animation-delay: -2s; }
        .matchday-hero .hero-particle-3 { top: 60%; left: 5%; animation-delay: -4s; }
        .matchday-hero .hero-particle-4 { top: 80%; right: 10%; animation-delay: -6s; }
        .matchday-hero .hero-particle-5 { top: 15%; left: 40%; animation-delay: -8s; }
        .matchday-hero .hero-particle-6 { top: 70%; right: 25%; animation-delay: -10s; }
        .matchday-hero .hero-particle-7 { top: 30%; left: 20%; animation-delay: -1s; }
        .matchday-hero .hero-particle-8 { top: 50%; right: 30%; animation-delay: -3s; }
        .matchday-hero .hero-particle-9 { top: 85%; left: 30%; animation-delay: -5s; }
        .matchday-hero .hero-particle-10 { top: 40%; right: 5%; animation-delay: -7s; }
        .matchday-hero .hero-particle-11 { top: 5%; right: 40%; animation-delay: -9s; }
        .matchday-hero .hero-particle-12 { top: 90%; left: 15%; animation-delay: -11s; }
        @keyframes matchdayHeroOrbFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(.9); }
        }
        @keyframes matchdayHeroParticleFloat {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: .3; }
          25% { transform: translate(20px, -30px) scale(1.5); opacity: .8; }
          50% { transform: translate(-15px, 20px) scale(.8); opacity: .5; }
          75% { transform: translate(25px, 10px) scale(1.2); opacity: .7; }
        }
        @media (max-width: 1100px) {
          .matchday-hero h1 { font-size: 36px; }
          .matchday-hero .hero-headline-accent { font-size: 24px; }
        }
        @media (max-width: 1000px) and (min-width: 761px) {
          .matchday-hero { padding-left: 32px; padding-right: 32px; }
          .matchday-hero .hero-download { padding: 15px 16px; }
        }
        @media (max-width: 760px) {
          .matchday-hero { padding: 88px 24px 16px; }
          .matchday-hero .hero-grid { grid-template-columns: minmax(0, 1fr); gap: 8px; max-width: 580px; }
          .matchday-hero .hero-content { padding-bottom: 0; }
          .matchday-hero h1 { margin-bottom: 8px; }
          .matchday-hero .hero-actions { margin-top: 8px; }
          .matchday-hero .hero-download { padding: 16px; font-size: 16px; }
          .matchday-hero .hero-carousel { max-width: 560px; margin: 0 auto; }
          .matchday-hero .hero-bg-orb-1 { width: 500px; height: 500px; top: 35%; }
          .matchday-hero .hero-particles { opacity: .5; }
        }
        @media (max-width: 390px) {
          .matchday-hero { padding-left: 18px; padding-right: 18px; }
          .matchday-hero .hero-download { font-size: 16px; padding: 15px 16px; gap: 10px; }
          .matchday-hero .hero-carousel { --hero-phone-inset: 12px; }
          .matchday-hero .hero-carousel-caption { bottom: -5%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .matchday-hero .hero-bg-orb, .matchday-hero .hero-particle { animation: none; }
          .matchday-hero .hero-design, .matchday-hero .hero-download { transition: none !important; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
